import { useParams, useOutletContext, useNavigate } from 'react-router-dom'
import { useVehicles } from '../../context/VehicleContext'

import { useEffect, useState } from 'react'
import VehicleCard from '../../components/Card/VehicleCard'
import AddVehicleCard from '../../components/Card/AddVehicleCard'
import AddVehicleToGarageModal from '../../components/Modal/AddVehicleToGarageModal'
import Button from '../../components/Button/Button'
import Typography from '../../components/Typography'
import SpinnerCircular from '../../components/SpinnerCircular'
import styled from 'styled-components'
import { theme } from '../../theme'
import { useTranslation } from 'react-i18next'
import AddIcon from '../../components/Icons/AddIcon'
import axiosClient from '../../api/axiosClient'

export default function UserGarage() {
  const { t } = useTranslation('userProfile')
  const { userId } = useParams()
  const { profileUser, setProfileUser, isOwnProfile } = useOutletContext()
  const navigate = useNavigate()

  const { vehicles: ownVehicles, fetchVehicles, deleteVehicle, loading } = useVehicles()
  const [modalOpen, setModalOpen] = useState(false)
  const [editVehicle, setEditVehicle] = useState(null)
  const [likedSet, setLikedSet] = useState(new Set())
  const [likesReady, setLikesReady] = useState(false)

  // Sólo cargamos vehículos desde el backend si es nuestro propio perfil
  useEffect(() => {
    if (isOwnProfile) {
      fetchVehicles(userId)
    }
  }, [userId, isOwnProfile, fetchVehicles])

  // Prefetch estado de likes para garaje público
  useEffect(() => {
    const prefetchLikes = async () => {
      if (isOwnProfile) { setLikesReady(true); return }
      const ids = (profileUser?.vehicles || []).map(v => v._id).filter(Boolean)
      if (ids.length === 0) {
        setLikedSet(new Set())
        setLikesReady(true)
        return
      }
      try {
        const { data } = await axiosClient.get('/api/vehicles/likes/status', { params: { ids: ids.join(',') } })
        const set = new Set((data.likedIds || []).map(id => String(id)))
        setLikedSet(set)
      } catch (e) {
        setLikedSet(new Set())
      } finally {
        setLikesReady(true)
      }
    }
    setLikesReady(false)
    prefetchLikes()
  }, [isOwnProfile, profileUser?.vehicles])

  if (loading) {
    return (
      <LoadingContainer>
        <SpinnerCircular
          size={30}
          thickness={180}
          speed={100}
          color={theme.fill.brandMain}
          secondaryColor={theme.fill.defaultWeak}
        />
      </LoadingContainer>
    )
  }

  // Si no es nuestro perfil, usamos los vehículos que llegaron con profileUser
  const vehiclesToShow = isOwnProfile
    ? ownVehicles
    : (profileUser.vehicles || [])

  return (
    <Garage>
      <Container>
        <Header>
          <Typography $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as="h3">
            {isOwnProfile ? t('garage.title') : t('garage.titlePublicMsg')}
          </Typography>
          {isOwnProfile && (
            <Button $variant="outline" onClick={() => setModalOpen(true)}>
              <AddIcon fill={theme.colors.defaultMain} />
              {t('garage.button')}
            </Button>
          )}
        </Header>

        {vehiclesToShow.length > 0 ? (
          <Grid>
            {vehiclesToShow.map(v => (
              <VehicleCard
                key={v._id}
                vehicle={v}
                isOwnGarage={isOwnProfile}
                originContext="user-garage"
                initialLiked={likedSet.has(String(v._id))}
                likesReady={likesReady}
                openEditModal={() => { setEditVehicle(v); setModalOpen(true) }}
                handleDeleteVehicle={async () => {
                  const ok = await deleteVehicle(v._id)
                  if (ok) {
                    navigate(`/user/${userId}/garage`)
                  }
                }}
              />
            ))}
            {isOwnProfile && <AddVehicleCard onClick={() => setModalOpen(true)} />}
          </Grid>
        ) : (
          <EmptyState>
            <Typography color={theme.colors.defaultWeak}>
              {isOwnProfile
                ? t('garage.emptyState.title')
                : t('garage.emptyState.publicMsg')
              }
            </Typography>
          </EmptyState>
        )}

      </Container>

      {isOwnProfile && (
        <AddVehicleToGarageModal
          isOpen={modalOpen}
          vehicle={editVehicle}
          onClose={() => { setModalOpen(false); setEditVehicle(null) }}
          onVehicleAdded={(newVehicle, updatedId) => {
            // 1) Volver a recargar el contexto de ownVehicles
            fetchVehicles(userId);
            // 2) Actualizar también profileUser.vehicles en el layout
            setProfileUser(prev => {
              const prevList = prev.vehicles || [];
              // si venimos de edición, sustituimos
              if (updatedId) {
                return {
                  ...prev,
                  vehicles: prevList.map(v => v._id === updatedId ? newVehicle : v)
                };
              }
              // si es creación, añadimos al final
              return {
                ...prev,
                vehicles: [...prevList, newVehicle]
              };
            });
          }}
        />

      )}
    </Garage>
  )
}

const Garage = styled.section`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex; justify-content: center; align-items: center; padding: 2rem;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  padding-bottom: 0px;
  width: 100%;
  max-width: 1400px;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Grid = styled.ul`
  grid-template-columns: 1fr 1fr 1fr;
  display: grid;
  gap: 1rem;
  padding: 0;
  list-style: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns:  1fr 1fr;
  }  

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }  
`;

const EmptyState = styled.div`
  // display: flex;
  // flex-direction: column;
  // align-items: center;
  // justify-content: center;  
  // border-radius: ${({ theme }) => theme.radius.xs};
  // border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
  // background: var(--bg-default-main, #FFF);
  // padding: ${({ theme }) => theme.sizing.sm};

  display: flex;
  padding: 80px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-sm, 16px);
  align-self: stretch;
  border-radius: 16px;
  border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
`;
